import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ReportRequest} from '../../../shared/domain/report/report-request.model';
import {MomentDateUtils} from '../../../../shared/utils/moment-date-utils';
import {DateRangeType} from '../../../../shared/domain/date-range-type.enum';
import {DateRangeHolder} from '../../../../shared/domain/date-range-holder.model';
import {ReportDataSourceKL} from '../../../shared/domain/report/report-data-source.enum';
import {ReportDimension, ReportDimensionKL} from '../../../shared/domain/report/report-dimension.enum';
import {SortUtils} from '../../../../shared/utils/sort-utils';
import {ReportMetric, ReportMetricsAll} from '../../../shared/domain/report/report-metric.enum';
import {BridgeAdUnit} from '../../../../shared/domain/bridge-ad-unit.model';
import {DemandPartner} from '../../../../shared/domain/demand-partner.model';
import {Publisher} from '../../../../shared/domain/publisher.model';
import {RolePermissionService} from '../../../../shared/auth/rbac/role-permission.service';
import {RolePermission} from '../../../../shared/auth/rbac/role-permission';
import {ActivityStatusTypeLabelKL, ActivityStatusTypeLabelKLPublisher} from '../../../../shared/domain/activity-status.model';
import {EbChannelDPs} from '../../../shared/domain/demand-partner.enum';
import {ReportType, ReportTypeKL} from '../../../shared/domain/report/report-type.enum';

@Component({
  selector: 'cm-report-builder-config',
  templateUrl: './report-builder-config.component.html',
  styleUrls: ['./report-builder-config.component.scss']
})
export class ReportBuilderConfigComponent implements OnInit {

  @Output()
  configUpdated = new EventEmitter<ReportRequest>();

  @Input()
  config: ReportRequest;

  ebChannelDps = EbChannelDPs;

  allowedDateRangeTypes = [
    DateRangeType.TODAY,
    DateRangeType.YESTERDAY,
    DateRangeType.THIS_WEEK,
    DateRangeType.LAST_7_DAYS,
    DateRangeType.THIS_MONTH,
    DateRangeType.LAST_30_DAYS,
    DateRangeType.LAST_MONTH,
    DateRangeType.LAST_3_MONTHS,
  ];
  maxDate = MomentDateUtils.today();
  dataSources = ReportDataSourceKL();
  reportTypes = ReportTypeKL().filter(rt => rt.key !== ReportType.DEMAND_PARTNER);

  publisherActivityStatuses = ActivityStatusTypeLabelKLPublisher();
  adUnitActivityStatuses = ActivityStatusTypeLabelKL();
  dimensions = [];

  // metrics
  generalMetrics = ReportMetricsGeneral();
  kpiMetrics = ReportMetricsKPI();
  statsMetrics = ReportMetricsStats();
  financialMetrics = ReportMetricsFinancial();
  engagementMetrics = ReportMetricsEngagement();
  exchangeMetrics = ReportMetricsExchange();

  // metrics filtered
  generalMetricsFiltered = ReportMetricsGeneral();
  kpiMetricsFiltered = ReportMetricsKPI();
  statsMetricsFiltered = ReportMetricsStats();
  financialMetricsFiltered = ReportMetricsFinancial();
  engagementMetricsFiltered = ReportMetricsEngagement();
  exchangeMetricsFiltered = ReportMetricsExchange();

  site = ReportMetric.SITE;
  appBundle = ReportMetric.APP_BUNDLE;

  siteDimension = ReportDimension.SITE;
  appBundleDimension = ReportDimension.APP_BUNDLE;

  UI = {
    metricsTotal: 0
  };

  metricsFilter: string;

  RolePermission = RolePermission;

  constructor(private permissions: RolePermissionService) {
  }

  ngOnInit(): void {
    this.dimensions = ReportDimensionKL()
      .filter(d => [
        ReportDimension.DAY,
        ReportDimension.MONTH,
        ReportDimension.YEAR,
        ReportDimension.WIDGET,
        ReportDimension.AD_UNIT,
       //ReportDimension.SITE,
       //ReportDimension.APP_BUNDLE,
        ReportDimension.PUBLISHER,
        ReportDimension.WIDGET_TYPE,
        ReportDimension.WIDGET_SIZE,
        ReportDimension.CONTENT_TYPE,
        ReportDimension.DEMAND_PARTNER,
        ReportDimension.TIPALTI_ID
      ].includes(d.key))
      .sort(SortUtils.propertyComparatorString('label'));
    this.UI.metricsTotal = ReportMetricsGeneral()
      .concat(ReportMetricsStats())
      .concat(ReportMetricsFinancial())
      .concat(ReportMetricsEngagement())
      .concat(ReportMetricsKPI())
      .length;
  }

  onDateRangeChanged(range: DateRangeHolder) {
    this.config.filters.dateFrom = range.dateFrom;
    this.config.filters.dateTo = range.dateTo;
    this.config.filters.dateRange = range.dateRangeType;
  }

  dimensionChange(d: ReportDimension, selected: boolean) {
    const metric = <ReportMetric> Object.keys(ReportMetric).find(k => k === d.toString());
    if (selected) {
      if (this.dimensions.indexOf(d) < 0) {}
      this.config.dimensions.push(d);
      if (metric) {
        this.metricChange(metric, true);
      }
    } else {
      this.config.dimensions.splice(this.config.dimensions.indexOf(d), 1);
      if (metric) {
        this.metricChange(metric, false);
      }
    }
    this.configUpdated.emit(this.config);
  }

  metricChange(m: ReportMetric, selected: boolean) {
    if (selected) {
      this.config.metrics.push(m);
    } else {
      this.config.metrics.splice(this.config.metrics.indexOf(m), 1);
    }
    this.configUpdated.emit(this.config);
  }

  metricsFilterChange() {
    const metricsFilter = m => this.metricsFilter ? m.label.toLowerCase().includes(this.metricsFilter.toLowerCase()) : true;
    this.kpiMetricsFiltered = this.kpiMetrics.filter(metricsFilter);
    this.generalMetricsFiltered = this.generalMetrics.filter(metricsFilter);
    this.statsMetricsFiltered = this.statsMetrics.filter(metricsFilter);
    this.financialMetricsFiltered = this.financialMetrics.filter(metricsFilter);
    this.engagementMetricsFiltered = this.engagementMetrics.filter(metricsFilter);
    this.exchangeMetricsFiltered = this.engagementMetrics.filter(metricsFilter);
  }

  filterAdUnitChange(units: BridgeAdUnit[]) {
    this.config.filters.adUnitIds = units.map(u => u.id);
  }

  filterDemandPartnerChange(dp: DemandPartner[]) {
    this.config.filters.demandPartnerIds = dp.map(u => u.name);
    this.config.filters.demandPartnerDisplayNames = dp.map(u => u.displayName);
    this.config.filters.cxDemandCompanyIds = dp.map(u => u.cxDemandCompanyId);

  }

  filterPublisherChange(pubs: Publisher[]) {
    this.config.filters.publisherIds = pubs.map(u => u.id);
  }
  filterEbPublisherChange(pubs: Publisher[]) {
    this.config.filters.ebAssociatedPublisherIds = pubs.map(u => u.id);
  }

  onBidderRadioSelected(e) {
    const val = e.value;
    if (!val) {
      this.config.filters.bidder = null;
    } else {
      this.config.filters.bidder = val === 'bidder';
    }
  }

}


export const ReportMetricsGeneral = () => ReportMetricsAll().filter(metric => [
  ReportMetric.DAY,
  ReportMetric.YEAR,
  ReportMetric.BRIDGE_AD_UNIT_CREATION_DATE,
  ReportMetric.MONTH,
  ReportMetric.WIDGET,
  ReportMetric.WIDGET_TYPE,
  ReportMetric.WIDGET_SIZE,
  ReportMetric.CONTENT_TYPE,
  ReportMetric.AD_UNIT_CUSTOM_NAME,
  ReportMetric.PUBLISHER,
  ReportMetric.DEMAND_PARTNER,
  ReportMetric.ACTIVITY_STATUS,
  ReportMetric.PUBLISHER_ACTIVITY_STATUS,
  ReportMetric.PUBLISHER_IOEXECUTEDDATE,
  ReportMetric.TAXONOMY,
  ReportMetric.SALES_REPRESENTATIVE,
  ReportMetric.ACCOUNT_REPRESENTATIVE_EMAIL,
  ReportMetric.VISITS,
  ReportMetric.BOUNCE_RATE,
  ReportMetric.SW_RANK,
  ReportMetric.SW_SUBCATEGORY,
  ReportMetric.SW_CATEGORY,
  ReportMetric.EB_ASSOCIATED_PUBLISHER_NAME,
  ReportMetric.SITE_MANAGEMENT,
  ReportMetric.PUBLISHER_TIER,
  ReportMetric.TIPALTI_PAYABLE,
  ReportMetric.TIPALTI_COMPANY_NAME
].includes(metric.key));

export const ReportMetricsKPI = () => ReportMetricsAll().filter(metric => [
  ReportMetric.RPM,
  ReportMetric.ECPM,
  ReportMetric.RCPM,
  ReportMetric.MARGIN,
  ReportMetric.FILLED,
  ReportMetric.AD_IMPRESSION_RATIO,
  ReportMetric.REFRESH_RATE,
].includes(metric.key));

export const ReportMetricsStats = () => ReportMetricsAll().filter(metric => [
  ReportMetric.IMPRESSIONS,
  ReportMetric.IMPRESSION_CORRECTIONS,
  ReportMetric.PIXEL_IMPRESSIONS,
  ReportMetric.DFP_DOWNLOADED_IMPRESSIONS,
  ReportMetric.FILLED_IMPRESSIONS,
  ReportMetric.AD_REQUESTS,
  ReportMetric.IMPRESSION_CORRECTION_FACTOR,
  ReportMetric.WIDGET_REQUESTS,
  ReportMetric.EBDA_IMPRESSIONS,
  ReportMetric.EBDA_VIDEO_IMPRESSIONS,
  ReportMetric.CONSTANT_BID,
  ReportMetric.OVERBID,
].includes(metric.key));

export const ReportMetricsFinancial = () => ReportMetricsAll().filter(metric => [
  ReportMetric.REVENUE,
  ReportMetric.PUBLISHER_REVENUE,
  ReportMetric.PROFIT,
  ReportMetric.PASSBACK_REVENUE,
  ReportMetric.EBDA_REVENUE,
  ReportMetric.EBDA_VIDEO_REVENUE,
].includes(metric.key));

export const ReportMetricsEngagement = () => ReportMetricsAll().filter(metric => [
  ReportMetric.IN_VIEW_DFP,
  ReportMetric.IN_VIEW_ADX,
  ReportMetric.IN_VIEW_TOTAL,
].includes(metric.key));

export const ReportMetricsExchange = () => ReportMetricsAll().filter(metric => [
    ReportMetric.SB_REVENUE,
    ReportMetric.SB_REVENUE_DISC,
    ReportMetric.SB_IMPRESSIONS,
    ReportMetric.SB_IMPRESSIONS_DISC,
    ReportMetric.SB_REQUESTS,
    ReportMetric.SB_REQUESTS_DISC,
    ReportMetric.SB_PARTNER_REQUESTS,
    ReportMetric.SB_DOMAIN,
    ReportMetric.SB_OVERBID_REVENUE,
    ReportMetric.SB_FIRST_IMPRESSION_REVENUE,
    ReportMetric.SB_CONSTANT_BID_REVENUE,
].includes(metric.key));
